import { queryOptions, type QueryClient } from '@tanstack/react-query';
import type { ApiKey } from './model';
import { AUTH_STALE_TIME, authQueryKeys } from '../auth/queryKeys';
import { fetchApiKey, fetchApiKeys, fetchApiKeyUsage } from './repository';
import { browserTimeZone } from '../../lib/timeZone';

export const keyQueryKeys = {
  list: (subject: string) => [...authQueryKeys.subject(subject), 'keys', 'list'] as const,
  detail: (subject: string, id: string) => [...authQueryKeys.subject(subject), 'keys', 'detail', id] as const,
  usage: (subject: string, id: string, days: number, timeZone: string) =>
    [...authQueryKeys.subject(subject), 'keys', 'usage', id, days, timeZone] as const,
};

export const apiKeysQueryOptions = (subject: string) =>
  queryOptions({ queryKey: keyQueryKeys.list(subject), queryFn: fetchApiKeys, staleTime: AUTH_STALE_TIME });
export const apiKeyQueryOptions = (subject: string, id: string) =>
  queryOptions({ queryKey: keyQueryKeys.detail(subject, id), queryFn: () => fetchApiKey(id), staleTime: AUTH_STALE_TIME });
/** The zone is part of the key for the same reason it is on account usage: the
 *  same window bucketed in two zones is two different day series. */
export const apiKeyUsageQueryOptions = (subject: string, id: string, days = 30) => {
  const timeZone = browserTimeZone();
  return queryOptions({
    queryKey: keyQueryKeys.usage(subject, id, days, timeZone),
    queryFn: () => fetchApiKeyUsage(id, days, timeZone),
    staleTime: AUTH_STALE_TIME,
  });
};

export const cacheApiKey = (queryClient: QueryClient, subject: string, key: ApiKey): void => {
  queryClient.setQueryData(keyQueryKeys.detail(subject, key.id), key);
  queryClient.setQueryData<ApiKey[]>(keyQueryKeys.list(subject), (keys) =>
    keys
      ? keys.some((item) => item.id === key.id)
        ? keys.map((item) => item.id === key.id ? key : item)
        : [key, ...keys]
      : keys,
  );
};
